import {
  SPECIAL_FIELDS,
  SUPPORTED_VEEVA_CONTROL_TYPES,
  VEEVA_VAULT_TABLES,
  VeevaVaultLayoutSectionControl,
  VeevaVaultObjectField,
  VeevaVaultObjectLayoutSection,
  VeevaVaultObjectMetadata,
  VeevaVaultSectionLayoutItem,
  VeevaVaultSectionLayoutItemControl,
} from '../../CRMVeevaVaultTypes';
import { SYNC_ENTRY_FETCH_TYPE, SYNC_ENTRY_REQUEST_TYPE, SYNC_ENTRY_STATUS, SyncEntry } from '../../ISyncManifest';
import { CrmIntegrationType } from '@alucio/aws-beacon-amplify/src/models';
import { CRMUtil } from 'src/state/machines/CRM/util';
import * as logger from 'src/utils/logger';

type ControlSectionHandler = {
  [objectName: string]: {
    [controlReference: string]:
      (control: VeevaVaultLayoutSectionControl,
       controllerObjectMetadata: VeevaVaultObjectMetadata) => SyncEntry[];
  }
}

const HANDLED_CONTROL_SECTIONS: ControlSectionHandler = {
  [VEEVA_VAULT_TABLES.CALL]: {
    [SUPPORTED_VEEVA_CONTROL_TYPES.ATTENDEE_SECTION]: handleAttendeesControlSection,
    [SUPPORTED_VEEVA_CONTROL_TYPES.MEDICAL_DISCUSSION_SECTION]:
    handleChildControlSection(VEEVA_VAULT_TABLES.MEDICAL_DISCUSSION),
    [SUPPORTED_VEEVA_CONTROL_TYPES.CALL_DETAIL_SECTION]: handleChildControlSection(VEEVA_VAULT_TABLES.CALL_DETAIL),
    [SUPPORTED_VEEVA_CONTROL_TYPES.CALL_DISCUSSION_SECTION]:
    handleChildControlSection(VEEVA_VAULT_TABLES.CALL_DISCUSSION),
  },
};

type SpecialControlHandler = {
  [tableName: string]: {
    [fieldName: string]: (
      objectMetadata: VeevaVaultObjectMetadata,
      fieldMetadata: VeevaVaultObjectField) => SyncEntry[];
  }
}

export const SPECIAL_FIELD_HANDLER: SpecialControlHandler = {
  [VEEVA_VAULT_TABLES.MEDICAL_DISCUSSION]: {
    'product__v': handleUserProductsField,
  },
  [VEEVA_VAULT_TABLES.CALL_DETAIL]: {
    'product__v': handleUserProductsField,
  },
  [VEEVA_VAULT_TABLES.CALL_DISCUSSION]: {
    'product__v': handleUserProductsField,
  },
  [VEEVA_VAULT_TABLES.MEDICAL_INSIGHT]: {
    'interaction__v': handleGetRecentInteractionsEntry,
  },
  [VEEVA_VAULT_TABLES.MEDICAL_INQUIRY]: {
    'call2__v': handleGetRecentInteractionsEntry,
  },
}

type ControlItemHandler = {
  [tableName: string]: {
    [fieldName: string]: (
        itemControl: VeevaVaultSectionLayoutItemControl,
        objectMetadata: VeevaVaultObjectMetadata) => SyncEntry[];
  }
}

export const CONTROL_ITEM_HANDLER: ControlItemHandler = {
  [VEEVA_VAULT_TABLES.MEDICAL_INQUIRY]: {
    [SUPPORTED_VEEVA_CONTROL_TYPES.MEDICAL_INQUIRY_USER]: handleMedicalInquiryItemControl,
  },
};

export function handleLayoutControlItem(
  item: VeevaVaultSectionLayoutItem,
  controllerObjectMetadata: VeevaVaultObjectMetadata,
): SyncEntry[] {
  logger.veevaVaultSyncer.info(
    `Handling control item [${item.label}] for object [${controllerObjectMetadata.name}]...`);
  const additionalEntries: SyncEntry[] = [];
  const controls = item.controls || [];

  for (const control of controls) {
    const controlHandler = CONTROL_ITEM_HANDLER[controllerObjectMetadata.name]?.[control.reference];
    if (controlHandler) {
      logger.veevaVaultSyncer.info(`Found item control handler for [${control.reference}]...`);
      const entriesForControl = controlHandler(control, controllerObjectMetadata);
      additionalEntries.push(...entriesForControl);
    }
  }

  return additionalEntries;
}

export function handleLayoutControlSection(
  layoutControlSection: VeevaVaultObjectLayoutSection,
  controllerObjectMetadata: VeevaVaultObjectMetadata): SyncEntry[] {
  logger.veevaVaultSyncer.info(
    `Handling control section [${layoutControlSection.name}] for object [${controllerObjectMetadata.name}]...`);
  const additionalEntries: SyncEntry[] = [];
  const controls = layoutControlSection.properties?.controls || [];

  for (const control of controls) {
    const controlHandler = HANDLED_CONTROL_SECTIONS[controllerObjectMetadata.name]?.[control.reference];
    if (controlHandler) {
      logger.veevaVaultSyncer.info(`Found control handler for [${control.reference}]...`);
      const entriesForControl = controlHandler(control, controllerObjectMetadata);
      additionalEntries.push(...entriesForControl);
    }
  }

  return additionalEntries;
}

// QUERY OBTAINED FROM THEIR UI
const USERS_QUERY = 'SELECT id, name__v FROM user__sys WHERE  ' +
  "(status__v = 'active__v' AND system_owned_user__sys = FALSE) OR" +
  " (status__v != 'active__v' AND system_owned_user__sys = FALSE AND id = NULL) ORDER BY name__v ASC";

const MEDICAL_INQUIRY_USER_QUERY = 'SELECT id, name__v FROM user__sys WHERE  ' +
    'status__v = \'active__v\'  AND id != NULL AND user_type__v != NULL AND system_owned_user__sys = FALSE ' +
    'ORDER BY name__v ASC';

const USER_PRODUCTS_QUERY = 'SELECT id, name__v FROM product__v ' +
  'WHERE id IN (SELECT product__v FROM my_setup_products__vr WHERE ownerid__v = {UserId})' +
  " AND status__v = 'active__v' AND company_product__v = true AND no_details__v = false" +
  " AND product_type__v CONTAINS ('detail__v', 'detail_topic__v')";

const RECENT_INTERACTIONS_QUERY = 'SELECT id, name__v FROM call2__v ' +
  "WHERE status__v = 'active__v' ORDER BY modified_date__v DESC MAXROWS 500";

// ATTENDEES ARE ALREADY HANDLED IN THE FORM, BUT THERE'S A SPECIAL "PROPERTY" (BINDINGS)
// THAT INDICATES THAT A USER SEARCH MUST BE INCLUDED AS WELL
function handleAttendeesControlSection(
  control: VeevaVaultLayoutSectionControl,
  controllerObjectMetadata: VeevaVaultObjectMetadata): SyncEntry[] {
  const additionalEntries: SyncEntry[] = [];

  control.bindings.forEach((binding) => {
    // 'su' = SEARCH USER INPUT SHOULD BE INCLUDED
    if (binding.property === 'su' && binding.value === 'true') {
      additionalEntries.push(
        getUsersSyncEntry(
          `${controllerObjectMetadata.name}-LOOKUP-${SPECIAL_FIELDS.USERS_ATTENDEES}`,
          controllerObjectMetadata.name,
          SPECIAL_FIELDS.USERS_ATTENDEES,
          controllerObjectMetadata.name,
          USERS_QUERY,
        ));
    }
  });

  return additionalEntries;
}

function getUsersSyncEntry(
  id: string,
  fieldName: string,
  lookupApiName: string,
  parentId: string,
  query: string,
): SyncEntry {
  return {
    url: '',
    id,
    type: CrmIntegrationType.VEEVA_VAULT,
    subType: SYNC_ENTRY_REQUEST_TYPE.LOOKUP,
    fetchType: SYNC_ENTRY_FETCH_TYPE.SOQL,
    status: SYNC_ENTRY_STATUS.PENDING,
    parameters: {
      query,
      parentId,
      lookupApiName,
      apiName: fieldName,
    },
    lastSynced: new Date().getTime(),
  };
}

function handleMedicalInquiryItemControl(
  _itemControl: VeevaVaultSectionLayoutItemControl,
  controllerObjectMetadata: VeevaVaultObjectMetadata,
): SyncEntry[] {
  return [
    getUsersSyncEntry(
      `${controllerObjectMetadata.name}-LOOKUP-${SPECIAL_FIELDS.MEDICAL_INQUIRY_USER}`,
      SPECIAL_FIELDS.MEDICAL_INQUIRY_USER,
      SPECIAL_FIELDS.MEDICAL_INQUIRY_USER,
      controllerObjectMetadata.name,
      MEDICAL_INQUIRY_USER_QUERY,
    )];
}

function handleChildControlSection(apiName: string) {
  return (_control: VeevaVaultLayoutSectionControl, metadata: VeevaVaultObjectMetadata): SyncEntry[] => [{
    id: apiName,
    url: '',
    type: CrmIntegrationType.VEEVA_VAULT,
    subType: SYNC_ENTRY_REQUEST_TYPE.TABLE,
    fetchType: SYNC_ENTRY_FETCH_TYPE.REGULAR,
    status: SYNC_ENTRY_STATUS.PENDING,
    lastSynced: new Date().getTime(),
    parameters: { parentId: metadata.name, apiName },
  }];
}

export function handleUserProductsField(
  objectMetadata: VeevaVaultObjectMetadata,
  fieldMetadata: VeevaVaultObjectField): SyncEntry[] {
  const userId = CRMUtil.getAuthInformation()?.userInfo?.id;
  const query = USER_PRODUCTS_QUERY.replace('{UserId}', `'${userId}'`);

  return [{
    id: `${objectMetadata.name}-LOOKUP-${fieldMetadata.name}`,
    url: '',
    type: CrmIntegrationType.VEEVA_VAULT,
    subType: SYNC_ENTRY_REQUEST_TYPE.LOOKUP,
    fetchType: SYNC_ENTRY_FETCH_TYPE.SOQL,
    status: SYNC_ENTRY_STATUS.PENDING,
    lastSynced: new Date().getTime(),
    parameters: {
      apiName: objectMetadata.name,
      lookupApiName: fieldMetadata.name,
      parentId: objectMetadata.name,
      query,
    },
  }];
}

export function handleGetRecentInteractionsEntry(
  objectMetadata: VeevaVaultObjectMetadata,
  fieldMetadata: VeevaVaultObjectField): SyncEntry[] {
  return [{
    id: `${objectMetadata.name}-LOOKUP-${fieldMetadata.name}`,
    url: '',
    type: CrmIntegrationType.VEEVA_VAULT,
    subType: SYNC_ENTRY_REQUEST_TYPE.LOOKUP,
    fetchType: SYNC_ENTRY_FETCH_TYPE.SOQL,
    status: SYNC_ENTRY_STATUS.PENDING,
    lastSynced: new Date().getTime(),
    parameters: {
      apiName: objectMetadata.name,
      lookupApiName: fieldMetadata.name,
      parentId: objectMetadata.name,
      query: RECENT_INTERACTIONS_QUERY,
    },
  }];
}
